import { useMemo } from "react";
import { ApiJob } from "../../../common/api/v1.1/types/jobs";
import { isValidXml } from "../Reading/StateManager/util";

import { Block, Image, State, TextBlock } from "../Reading/types";
import { blockIsTable } from "../Reading/util";
import {
  convertInnoTextToReadingHTML,
  convertReadingHTMLToInnodom,
  convertReadingTableBlockToInnodom,
  extractTableCells,
  extractTableCells2,
  recoverInnoTextLeniently,
} from "./innodom";

type RawPage = Readonly<{
  images: {
    height: number;
    width: number;
  };
}>;
type RawPageMeta = Readonly<{
  pages: ReadonlyArray<RawPage>;
}>;

const X_MIN_INDEX = 0;
const Y_MIN_INDEX = 1;
const X_MAX_INDEX = 2;
const Y_MAX_INDEX = 3;
export type RawZone = Readonly<{
  blockId: string;
  class: string;
  continuationId: string;
  continuedFromId: string;
  h: number;
  "original-bbox": [number, number, number, number];
  pg: number;
  scores: number;
  subclass: string;
  "table-xml"?: string;
  "table-structure"?: any;
  text?: string;
  w: number;
}>;
export type RawZones = Readonly<{
  blocks: ReadonlyArray<RawZone>;
}>;

const isRawPage = (input: any): input is RawPage => {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  const { images } = input;
  if (typeof images !== "object" || images === null || Array.isArray(images)) {
    return false;
  }
  const { height, width } = images;
  return typeof height === "number" && typeof width === "number";
};
const isRawPageMeta = (input: any): input is RawPageMeta => {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  if (Array.isArray(input.pages)) {
    for (const rawPage of input.pages) {
      if (!isRawPage(rawPage)) {
        return false;
      }
    }
    return true;
  }
  return false;
};

const isRawZone = (input: any): input is RawZone => {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  // validate input.class is a valid zone type
  const zoneType = input.class;
  if (typeof zoneType !== "string") {
    return false;
  }
  if (typeof input.pg !== "number") {
    return false;
  } else if (input.text !== undefined && typeof input.text !== "string") {
    return false;
  } else if (!Array.isArray(input["original-bbox"])) {
    return false;
  }
  if (input["original-bbox"].length !== 4) {
    return false;
  }
  for (let i = 0; i < 4; i++) {
    if (typeof input["original-bbox"][i] !== "number") {
      return false;
    }
  }
  return true;
};
const isRawZones = (input: any): input is RawZones => {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  if (Array.isArray(input.blocks)) {
    for (const block of input.blocks) {
      if (!isRawZone(block)) {
        return false;
      }
    }
    return true;
  }
  return false;
};

const mapRawPageMeta = (pageUris: Array<string>) => (rawPage: RawPage, index: number): Image => {
  const page: Image = {
    height: rawPage.images.height,
    src: pageUris[index],
    width: rawPage.images.width,
  };
  if (isNaN(page.height) || page.height < 1) {
    throw new Error(`Invalid height for page ${index + 1}.`);
  }
  if (isNaN(page.width) || page.width < 1) {
    throw new Error(`Invalid width for page ${index + 1}.`);
  }
  return page;
};

const mapRawZones = (images: Array<Image>, offset: number) => (
  rawZone: RawZone,
  index: number,
): Block | undefined => {
  const zone = {
    id: `block-${index}`,
    maxX: rawZone["original-bbox"][X_MAX_INDEX],
    maxY: rawZone["original-bbox"][Y_MAX_INDEX],
    minX: rawZone["original-bbox"][X_MIN_INDEX],
    minY: rawZone["original-bbox"][Y_MIN_INDEX],
    originalZone: rawZone,
    page: parseInt(rawZone.pg.toString(), 10) - 1 - offset,
    continuationId: rawZone.continuationId ?? "",
    continuedFromId: rawZone.continuedFromId ?? "",
  };
  if (isNaN(zone.page) || zone.page < 0 || zone.page >= images.length) {
    throw new Error(`Page for zone ${index + 1} is outside of range.`);
  }
  if (isNaN(zone.maxX) || zone.maxX < 0 || zone.maxX > images[zone.page].width) {
    throw new Error(`Maximum x value for zone ${index + 1} is invalid.`);
  }
  if (isNaN(zone.minX) || zone.minX < 0 || zone.minX > images[zone.page].width) {
    throw new Error(`Minimum x value for zone ${index + 1} is invalid.`);
  }
  if (isNaN(zone.maxY) || zone.maxY < 0 || zone.maxY > images[zone.page].height) {
    throw new Error(`Maximum y value for zone ${index + 1} is invalid.`);
  }
  if (isNaN(zone.minY) || zone.minY < 0 || zone.minY > images[zone.page].height) {
    throw new Error(`Minimum y value for zone ${index + 1} is invalid.`);
  }
  if (zone.minX > zone.maxX) {
    throw new Error(`X values reversed for zone ${index + 1}.`);
  }
  if (zone.minY > zone.maxY) {
    throw new Error(`Y values reversed for zone ${index + 1}.`);
  }
  if (rawZone.class === "table") {
    return {
      ...zone,
      //cells: extractTableCells(rawZone["table-xml"], zone.id),
      // For back-compabitility, though extractTableCells doesn't work in some cases (?)
      cells: !!rawZone["table-structure"] ? extractTableCells2(rawZone["table-structure"], zone.id) : extractTableCells(rawZone["table-xml"], zone.id)
    };
  }
  let text = "";
  let parseFailed = false;
  if (rawZone.text !== undefined) {
    try {
      text = convertInnoTextToReadingHTML(rawZone.text);
    } catch (err) {
      // A single malformed block (e.g. a corrupt font attribute from an older job)
      // must not crash the entire workbench load. Fall back to a lenient recovery so
      // the job still opens; the block is flagged invalid so the user can fix and re-save it.
      console.error(`Failed to parse reading text for block ${index + 1}; using lenient recovery.`, err);
      text = recoverInnoTextLeniently(rawZone.text);
      parseFailed = true;
    }
  }
  const textBlock: TextBlock = {
    ...zone,
    isCode: false,
    text,
    validXml: !parseFailed && isValidXml(text),
  };
  return textBlock;
};

const removeInvalidBlocks = (block: any): block is Block => !!block;

export const useInitialState = (
  pageMeta: any,
  pageUris: Array<string>,
  rawZones: any,
  job: ApiJob
): [ReadonlyArray<Image>, ReadonlyArray<Block>] =>
  useMemo((): [Array<Image>, Array<Block>] => {
    if (!isRawPageMeta(pageMeta)) {
      throw new Error("Invalid page metadata.");
    }
    if (!isRawZones(rawZones)) {
      throw new Error("Invalid zone metadata.");
    }
    let images: Array<Image> = pageMeta.pages.map(mapRawPageMeta(pageUris));
    let offset = 0;

    if (job.parent_id) {
      images = images.slice(job.metadata.first_page -1, job.metadata.last_page -1);
      offset = job.metadata.first_page - 1;
    }
    const blocks: Array<Block> = rawZones.blocks
      .map(mapRawZones(images, offset))
      .filter(removeInvalidBlocks);
    return [images, blocks];
  }, []);

export const mapStateToRawZones = (state: State): RawZones => {
  return {
    blocks: state.blockOrder.map(
      (blockId: string): RawZone => {
        const block = state.blocks[blockId];
        if (blockIsTable(block)) {
          return {
            ...block.originalZone,
            blockId: block.id,
            continuationId: block.continuationId,
            continuedFromId: block.continuedFromId,
            "table-xml": convertReadingTableBlockToInnodom(block),
          };
        }
        return {
          ...block.originalZone,
          blockId: block.id,
          continuationId: block.continuationId,
          continuedFromId: block.continuedFromId,
          text: convertReadingHTMLToInnodom(block.text),
        };
      },
    ),
  };
};
